import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useProducts } from '../../context/ProductContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  CubeIcon,
  BuildingStorefrontIcon,
  PhotoIcon,
  ClockIcon,
  InformationCircleIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const ProductView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const getRoutePath = useRoutePath();
  const { hideLoading } = useLoading();
  const { products, getProductById } = useProducts();

  const [product, setProduct] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const hasFetchedProduct = useRef(false);

  const fetchProduct = useCallback(async (id) => {
    if (!id || hasFetchedProduct.current === id) return;
    hasFetchedProduct.current = id; // store current id
     
    setIsFetching(true);

    try {
      const response = await getProductById(id);
      if (response?.status === 200 && response?.data) {
        setProduct(response.data);
      } else {
        setProduct(null);
      }
    } catch (error) {
      console.error('Error fetching product data:', error);
      setProduct(null);
    } finally {
      setIsFetching(false);
      hideLoading();
    }
  }, [getProductById, hideLoading]);

  useEffect(() => {
    // Quick resolve from context list
    if (id && products?.data?.length && !product) {
      const existing = products.data.find(
        (item) => Number(item.id) === Number(id)
      );
      if (existing && !hasFetchedProduct.current) {
        setProduct(existing);
      }
    }
   
    fetchProduct(id);
  }, [id, products, product, fetchProduct]);

  const handleEdit = () => {
    navigate(getRoutePath(`/products/edit/${id}`));
  };

  /* ─── helpers ────────────────────────────────────────── */

  const getStatusBadge = (value) => {
    const isActive = value === 1 || value === true;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {isActive ? (<><CheckCircleIcon className="h-4 w-4 mr-1" />Active</>) : (<><XCircleIcon className="h-4 w-4 mr-1" />Inactive</>)}
      </span>
    );
  };

  const getBoolBadge = (value, yesLabel = 'Yes', noLabel = 'No') => {
    const isTrue = value === 1 || value === true;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${isTrue ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
        {isTrue ? (<><CheckCircleIcon className="h-4 w-4 mr-1" />{yesLabel}</>) : (<><XCircleIcon className="h-4 w-4 mr-1" />{noLabel}</>)}
      </span>
    );
  };

  const fmt = (v) => {
    if (v === null || v === undefined || v === '') return 'N/A';
    return v;
  };

  const fmtCurrency = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return 'N/A';
    return `$${n.toFixed(2)}`;
  };

  /* ─── loading / not-found states ─────────────────────── */

  if (!isFetching && !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <p className="text-gray-600 mb-6">The product you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <button onClick={() => navigate(getRoutePath('/products'))} className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors">
            <ArrowLeftIcon className="h-5 w-5 mr-2" />Back to Products
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-8 w-8 border-2 border-[#009efb] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading product information...</p>
        </div>
      </div>
    );
  }

  /* ─── derived data ───────────────────────────────────── */

  const title = product.name || 'Product Details';
  const categoryName = product.product_category?.name || 'N/A';
  const displayImages = Array.isArray(product.display_images)
    ? product.display_images.filter(img => img.status !== 404)
    : [];
  const relatedProducts = Array.isArray(product.related_products) ? product.related_products : [];
  
  const hasEquipmentDetails = product.is_equipment_item;
  const hasWarranty = product.is_extended_warranty;
  const hasInventory = product.is_inventory_item;
  const hasCommission = product.is_pays_commission;
  const hasBonus = product.is_pays_bonus;

  const hasVendorInfo =
    product.primary_vendor_id || product.secondary_vendor_id || product.tertiary_vendor_id || product.purchase_description;

  /* ─── render ─────────────────────────────────────────── */

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">

        {/* ─── Header ─── */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            <div className="flex items-start md:items-center space-x-3">
              {displayImages.length > 0 ? (
                <img
                  src={displayImages[0].path || displayImages[0].url}
                  alt={title}
                  className="h-12 w-12 rounded-lg object-cover border border-gray-200"
                />
              ) : (
                <div className="p-2 bg-[#009efb] rounded-lg">
                  <CubeIcon className="h-6 w-6 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center flex-wrap gap-2">
                  <span>{title}</span>
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>{getStatusBadge(product.active)}</span>
                  {categoryName !== 'N/A' && (
                    <span className="text-gray-500">Category: {categoryName}</span>
                  )}
                  {product.sku && (
                    <span className="text-gray-500">SKU: {product.sku}</span>
                  )}
                  {product.type && (
                    <span className="text-gray-500">Type: {product.type}</span>
                  )}
                  {product.formated_created_at && (
                    <span className="text-gray-500">Created: {product.formated_created_at}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button onClick={() => navigate(getRoutePath('/products'))} className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto">
                <ArrowLeftIcon className="h-4 w-4 mr-2" />Back to List
              </button>
              <button onClick={handleEdit} className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors w-full sm:w-auto">
                <PencilIcon className="h-4 w-4 mr-2" />Edit Product
              </button>
            </div>
          </div>
        </div>

        {/* ─── Main Grid ─── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* ─── Left Column ─── */}
          <div className="xl:col-span-2 space-y-6">

            {/* Product Details Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <InformationCircleIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">Product Details</h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{fmt(product.name)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{categoryName}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{fmt(product.sku)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">UPC</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{fmt(product.upc)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{fmt(product.model)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Part Number</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{fmt(product.part_number)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{fmt(product.type)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="p-3 bg-gray-50 rounded-lg">{getStatusBadge(product.active)}</div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-900 min-h-[60px] whitespace-pre-wrap">
                    {product.description || 'No description provided.'}
                  </div>
                </div>
              </div>
            </div>

            {/* Equipment Details Card (conditional) */}
            {hasEquipmentDetails && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <WrenchScrewdriverIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Equipment Details</h2>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                        {product.manufacturer?.name || fmt(product.manufacturer_id)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Type</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                        {product.equipment_type?.name || fmt(product.equipment_type_id)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Warranty Card (conditional) */}
            {hasWarranty !==0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Extended Warranty</h2>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Warranty Provider</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                        {product.warranty_provider?.name || fmt(product.warranty_provider_id)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Warranty Expires</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                        {fmt(product.warranty_expires)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pricing & Inventory Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">Pricing &amp; Inventory</h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-sm font-medium text-gray-700">Regular Price</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">{fmtCurrency(product.regular_price)}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-sm font-medium text-gray-700">Member Price</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">{fmtCurrency(product.member_price)}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-sm font-medium text-gray-700">Average Cost</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">{fmtCurrency(product.average_cost)}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <span className="text-sm font-medium text-gray-700">Inventory Item:</span>
                  {getBoolBadge(product.is_inventory_item)}
                </div>
                
                {hasInventory !== 0 && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200 mt-2">
                    <h4 className="text-md font-semibold text-green-900 mb-3">Inventory Management</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Reorder Point</label>
                        <div className="text-sm font-medium text-gray-900">{fmt(product.reorder_point)}</div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Reorder Quantity</label>
                        <div className="text-sm font-medium text-gray-900">{fmt(product.reorder_quantity)}</div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Minimum Across Warehouses</label>
                        <div className="text-sm font-medium text-gray-900">{fmt(product.minimum_across_warehouses)}</div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Minimum Per Warehouse</label>
                        <div className="text-sm font-medium text-gray-900">{fmt(product.minimum_per_warehouses)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Commission Settings Card */}
            {(hasCommission || hasBonus || product.pays_hours) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <UserGroupIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Commission Settings</h2>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {/* Commission */}
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">Pays Commission:</span>
                    {getBoolBadge(product.is_pays_commission)}
                  </div>

                  {hasCommission && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Fixed Commission</label>
                          <div className="text-sm font-medium text-gray-900">{fmtCurrency(product.fixed_commission)}</div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Percentage Commission</label>
                          <div className="text-sm font-medium text-gray-900">
                            {product.percentage_commission != null ? `${product.percentage_commission}%` : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bonus */}
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">Pays Bonus:</span>
                    {getBoolBadge(product.is_pays_bonus)}
                  </div>

                  {hasBonus && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Fixed Bonus</label>
                          <div className="text-sm font-medium text-gray-900">{fmtCurrency(product.fixed_bonus)}</div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Percentage Bonus</label>
                          <div className="text-sm font-medium text-gray-900">
                            {product.percentage_bonus != null ? `${product.percentage_bonus}%` : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pays Hours */}
                  {product.pays_hours && (
                    <div className="flex items-center gap-3 pt-2">
                      <span className="text-sm font-medium text-gray-700">Pays Hours:</span>
                      <span className="inline-flex items-center text-sm font-medium text-gray-900">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
                        {product.pays_hours}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Vendor Information Card */}
            {hasVendorInfo && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <BuildingStorefrontIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Vendor Information</h2>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {product.purchase_description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Description</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-900 min-h-[60px] whitespace-pre-wrap">
                        {product.purchase_description}
                      </div>
                    </div>
                  )}

                  {product.primary_vendor_id && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Primary Vendor</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Vendor Name</label>
                          <div className="text-sm font-medium text-gray-900">
                            {product.primary_vendor?.name || fmt(product.primary_vendor_id)}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Purchase Price</label>
                          <div className="text-sm font-medium text-gray-900">{fmtCurrency(product.primary_vendor_purchase_price)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {product.secondary_vendor_id && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Secondary Vendor</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Vendor Name</label>
                          <div className="text-sm font-medium text-gray-900">
                            {product.secondary_vendor?.name || fmt(product.secondary_vendor_id)}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Purchase Price</label>
                          <div className="text-sm font-medium text-gray-900">{fmtCurrency(product.secondary_vendor_purchase_price)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {product.tertiary_vendor_id && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Tertiary Vendor</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Vendor Name</label>
                          <div className="text-sm font-medium text-gray-900">
                            {product.tertiary_vendor?.name || fmt(product.tertiary_vendor_id)}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Purchase Price</label>
                          <div className="text-sm font-medium text-gray-900">{fmtCurrency(product.tertiary_vendor_purchase_price)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ─── Right Column ─── */}
          <div className="space-y-6">

            {/* Product Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">Product Summary</h2>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  {getStatusBadge(product.active)}
                </div>
                {categoryName !== 'N/A' && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Category</span>
                    <span className="font-medium text-gray-900">{categoryName}</span>
                  </div>
                )}
                {product.sku && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">SKU</span>
                    <span className="font-medium text-gray-900">{product.sku}</span>
                  </div>
                )}
                {product.type && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Type</span>
                    <span className="font-medium text-gray-900">{product.type}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Regular Price</span>
                  <span className="font-medium text-gray-900">{fmtCurrency(product.regular_price)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Member Price</span>
                  <span className="font-medium text-gray-900">{fmtCurrency(product.member_price)}</span>
                </div>
                {product.is_equipment_item && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Equipment Item</span>
                    {getBoolBadge(product.is_equipment_item)}
                  </div>
                )}
                {product.is_extended_warranty && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Ext. Warranty</span>
                    {getBoolBadge(product.is_extended_warranty)}
                  </div>
                )}
                {product.formated_created_at && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1">Created At</div>
                    <div className="text-sm text-gray-900">{product.formated_created_at}</div>
                  </div>
                )}
                {product.formated_updated_at && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Last Updated</div>
                    <div className="text-sm text-gray-900">{product.formated_updated_at}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Media URL */}
            {product.media_url && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-lg font-semibold text-gray-900">Media URL</h2>
                </div>
                <div className="p-6">
                  <a
                    href={product.media_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#009efb] hover:text-[#0089db] break-all text-sm"
                  >
                    {product.media_url}
                  </a>
                </div>
              </div>
            )}

            {/* Images Card */}
            {displayImages.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center">
                    <PhotoIcon className="h-5 w-5 text-gray-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-900">Images ({displayImages.length})</h2>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-3">
                    {displayImages.map((image, index) => (
                      <a
                        key={index}
                        href={image.path || image.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block group"
                      >
                        <div className="relative overflow-hidden rounded-lg">
                          <img
                            src={image.path || image.url}
                            alt={`Product preview ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg group-hover:scale-105 transition-transform duration-200"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition duration-200 rounded-lg" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Related Products Card */}
            {relatedProducts.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center">
                    <CubeIcon className="h-5 w-5 text-gray-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-900">Related Products ({relatedProducts.length})</h2>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-2">
                  
                    {relatedProducts.map((rProduct, index) => (
                        
                      <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <CubeIcon className="h-4 w-4  mr-2 flex-shrink-0" />
                        <span className="text-sm font-medium  ">
                            
                            <Link target="_blank" className="text-[#009efb] hover:text-[#0089db]" to={getRoutePath(`/products/view/${rProduct?.id}`)}> 
                                {rProduct?.name}
                            </Link>
                          
                        </span>
                      </div>
                       
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductView;
